'use client';

import { useState, useEffect, useRef } from 'react';

// Danh sách các status hợp lệ (coin đang được giao dịch)
const ACTIVE_STATUS = ['TRADING', 'BREAK', 'HALT'];

export function useTopGainers(limit = 20) {
  const [gainers, setGainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeSymbols, setActiveSymbols] = useState(new Set());
  const wsRef = useRef(null);

  // Hàm lấy danh sách symbol đang hoạt động từ /exchangeInfo
  const fetchActiveSymbols = async () => {
    try {
      // Dùng endpoint cho USD-M Futures
      const response = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
      const data = await response.json();
      
      if (data && data.symbols && Array.isArray(data.symbols)) {
        // Lọc các symbol đang TRADING và kết thúc bằng USDT
        const activeSet = new Set();
        data.symbols.forEach(symbol => {
          // Kiểm tra status === 'TRADING' (đang giao dịch) và không bị delist
          // Một số symbol có thể có status khác như 'BREAK', 'HALT' - không lấy
          if (symbol.status === 'TRADING' && symbol.symbol.endsWith('USDT')) {
            activeSet.add(symbol.symbol);
          }
        });
        setActiveSymbols(activeSet);
        console.log(`✅ Found ${activeSet.size} active USDT pairs on Futures`);
        return activeSet;
      }
    } catch (err) {
      console.error('Failed to fetch active symbols from exchangeInfo:', err);
      return null;
    }
  };

  // Fetch top gainers từ Binance REST API (chỉ lấy coin đang active)
  const fetchTopGainersRest = async () => {
    try {
      // Lấy danh sách symbol active trước
      let activeSet = activeSymbols;
      if (activeSet.size === 0) {
        const fetchedSet = await fetchActiveSymbols();
        if (fetchedSet) activeSet = fetchedSet;
        else throw new Error('Cannot fetch active symbols');
      }
      
      // Lấy 24h ticker cho tất cả USDT pairs
      const response = await fetch('https://fapi.binance.com/fapi/v1/ticker/24hr');
      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid response from Binance API');
      }
      
      // Lọc: chỉ lấy USDT pairs, đang active, và có volume > 1M USDT
      const usdtPairs = data.filter(item => {
        const symbol = item.symbol;
        const volume = parseFloat(item.quoteVolume);
        // Kiểm tra: 
        // 1. Symbol kết thúc bằng USDT
        // 2. Symbol đang active trong exchangeInfo
        // 3. Volume > 1M USDT (tránh coin thanh khoản thấp)
        return symbol.endsWith('USDT') && 
               activeSet.has(symbol) && 
               volume > 1000000;
      });
      
      // Sắp xếp theo % thay đổi giảm dần
      const sorted = usdtPairs
        .map(item => ({
          symbol: item.symbol,
          price: parseFloat(item.lastPrice),
          change24h: parseFloat(item.priceChangePercent),
          volume24h: parseFloat(item.quoteVolume),
          high24h: parseFloat(item.highPrice),
          low24h: parseFloat(item.lowPrice),
          status: 'TRADING' // Xác nhận đang giao dịch
        }))
        .sort((a, b) => b.change24h - a.change24h)
        .slice(0, limit);
      
      setGainers(sorted);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch top gainers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket cho real-time ticker (cũng cần lọc active symbols)
  const connectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsUrl = 'wss://fstream.binance.com/ws/!ticker@arr';
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = async () => {
      console.log('Top gainers WebSocket connected');
      // Đảm bảo có danh sách active symbols
      if (activeSymbols.size === 0) {
        await fetchActiveSymbols();
      }
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!Array.isArray(data)) return;
        
        // Lọc chỉ lấy active symbols
        const usdtPairs = data
          .filter(item => {
            const symbol = item.s;
            const volume = parseFloat(item.q);
            return symbol.endsWith('USDT') && 
                   activeSymbols.has(symbol) && 
                   volume > 1000000;
          })
          .map(item => ({
            symbol: item.s,
            price: parseFloat(item.c),
            change24h: parseFloat(item.P),
            volume24h: parseFloat(item.q),
            high24h: parseFloat(item.h),
            low24h: parseFloat(item.l),
            status: 'TRADING'
          }))
          .sort((a, b) => b.change24h - a.change24h)
          .slice(0, limit);
        
        if (usdtPairs.length > 0) {
          setGainers(usdtPairs);
          setLastUpdate(new Date());
        }
        setError(null);
      } catch (err) {
        console.error('WebSocket message error:', err);
      }
    };
    
    ws.onerror = (err) => {
      console.error('Top gainers WebSocket error:', err);
      fetchTopGainersRest(); // Fallback to REST API
    };
    
    ws.onclose = () => {
      console.log('Top gainers WebSocket disconnected');
      setTimeout(() => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          connectWebSocket();
        }
      }, 30000);
    };
  };

  // Refresh dữ liệu (có thể cập nhật danh sách active symbols)
  const refresh = async () => {
    setLoading(true);
    await fetchActiveSymbols(); // Refresh danh sách active symbols
    await fetchTopGainersRest();
  };

  useEffect(() => {
    // Khởi tạo: lấy danh sách active symbols trước
    const init = async () => {
      await fetchActiveSymbols();
      await fetchTopGainersRest();
    };
    init();
    
    // Refresh mỗi 30 giây
    const interval = setInterval(fetchTopGainersRest, 30000);
    
    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [limit]);

  return { gainers, loading, error, lastUpdate, refresh };
}